// The free supporting statement checker (/supporting-statement-checker).
// Plain rules that run in the browser: nothing is sent anywhere. It checks
// what shortlisters check: every criterion in the person specification has
// evidence, the evidence is about the applicant ("I", with results), it fits
// the limit, and (for NHS jobs) the values show and no patient could be
// identified.

export type CheckStatus = 'good' | 'warn' | 'bad'

export interface CheckItem {
  id: string
  title: string
  status: CheckStatus
  detail: string
}

export interface CriterionResult {
  text: string
  status: 'covered' | 'partly' | 'missing'
  matched: string[]
  missing: string[]
}

export interface StatementReport {
  words: number
  characters: number
  checks: CheckItem[]
  criteria: CriterionResult[]
}

export interface StatementInput {
  statement: string
  personSpec: string
  limit?: number
  limitUnit: 'words' | 'characters'
  nhs: boolean
}

const STOPWORDS = new Set([
  'about', 'above', 'across', 'after', 'again', 'against', 'also', 'among', 'and', 'any', 'appropriate', 'area', 'areas',
  'ability', 'able', 'been', 'being', 'both', 'candidate', 'clear', 'clearly', 'could', 'criteria', 'demonstrable',
  'demonstrate', 'demonstrated', 'desirable', 'different', 'during', 'each', 'effective', 'effectively', 'either',
  'essential', 'evidence', 'excellent', 'experience', 'from', 'good', 'have', 'having', 'high', 'including', 'into',
  'knowledge', 'level', 'like', 'more', 'most', 'must', 'need', 'other', 'over', 'own', 'previous', 'proven', 'range',
  'relevant', 'required', 'requirements', 'role', 'should', 'skills', 'some', 'strong', 'such', 'than', 'that', 'their',
  'them', 'there', 'these', 'they', 'this', 'those', 'through', 'under', 'understanding', 'using', 'very', 'well',
  'what', 'when', 'where', 'which', 'while', 'will', 'with', 'within', 'without', 'work', 'working', 'would', 'your',
  'application', 'interview', 'assessment', 'post', 'post-holder', 'postholder', 'job', 'equivalent', 'similar',
])

// Headings and labels that appear in person specifications but aren't criteria.
const HEADING = /^(essential|desirable|criteria|qualifications?|education|experience|skills|knowledge|abilities|personal (qualities|attributes|skills)|other( requirements)?|requirements|assessment|method|values|training|special requirements|person specification)\b[\s:]*$/i
// Assessment-method markers at the end of a criterion line ("A", "A/I", "Application/Interview").
const ASSESSMENT_MARK = /[\s,(]*(\b(a|i|t|e|d|af|aw)(\s*[/,&]\s*(a|i|t|e|d|af))*\b|application( form)?(\s*[/,&]\s*interview)?|interview|essential|desirable)\)?\s*$/i

const GENERIC_CLAIMS = [
  'excellent communication skills', 'good communication skills', 'strong communication skills', 'team player', 'hard working', 'hard-working',
  'hardworking', 'passionate', 'self-motivated', 'self motivated', 'highly motivated', 'attention to detail', 'works well under pressure',
  'work well under pressure', 'i believe i would be', 'i feel i would be', 'i am confident that i', 'perfect candidate', 'ideal candidate',
  'go the extra mile', 'people person', 'fast learner', 'quick learner',
]

// An outcome in words, or a measured one ("30%", "£2,000", "within 20 minutes",
// "12 patients"). A bare number isn't enough: "Band 5" and "NEWS2" aren't results.
const RESULT_MARKERS = /\b(as a result|which meant|this meant|resulting in|resulted in|so that|led to|improv(ed|ing)|reduc(ed|ing)|increas(ed|ing)|saved|achiev(ed|ing)|completed|on time|feedback|praised|recognised|outcome)\b|\d+(\.\d+)?\s?(%|per ?cent)|£\s?\d|\b\d+\s?(minutes?|hours?|days?|weeks?|months?|patients?|people|customers?|clients?|calls?|cases?|staff|colleagues|students?|pupils?|applications?)\b/i

const NHS_VALUES: Array<[string, RegExp]> = [
  ['working together for patients', /\b(work(ed|ing)? together|team|colleague|multidisciplinary|mdt|handover|collaborat)/i],
  ['respect and dignity', /\b(respect|dignity|privacy|preferences|wishes)/i],
  ['commitment to quality of care', /\b(quality|safe(ty|ly)?|standards?|care plan|best practice|policy|procedures?|audit)\b/i],
  ['compassion', /\b(compassion|kind(ness)?|empath|reassur|comfort|distress|listen(ed|ing)?|calm(ly)?)/i],
  ['improving lives', /\b(improv(e|ed|ing) (the )?(lives|outcomes|care|experience)|wellbeing|well-being)/i],
  ['everyone counts', /\b(inclusi|equal|divers|fair(ly|ness)?|everyone)/i],
]

// Matching key for a word: long words by their first six letters, so
// "registered" meets "registration" and "escalate" meets "escalating".
const stem = (word: string) => word.length >= 6
  ? word.slice(0, 6)
  : word.replace(/(ings|ing|ies|ied|ed|es|s)$/, '').replace(/(.)\1$/, '$1')

const tokens = (text: string) => (text.toLowerCase().match(/[a-z][a-z'-]{2,}/g) || [])
  .map((w) => w.replace(/'s$/, ''))

function keyTerms(text: string): string[] {
  const seen = new Set<string>()
  const terms: string[] = []
  for (const word of tokens(text)) {
    if (word.length < 4 || STOPWORDS.has(word)) continue
    const s = stem(word)
    if (s.length < 3 || seen.has(s)) continue
    seen.add(s)
    terms.push(word)
  }
  return terms
}

export function parseCriteria(personSpec: string): string[] {
  return personSpec
    .split(/\r?\n|•|·|▪|●/)
    .map((line) => line.replace(/^[\s\-*–—•·\d.)]+/, '').replace(ASSESSMENT_MARK, '').trim())
    .filter((line) => line.split(/\s+/).length >= 3 && !HEADING.test(line))
    .slice(0, 30)
}

const countWords = (text: string) => (text.trim().match(/\S+/g) || []).length

export function checkStatement(input: StatementInput): StatementReport {
  const statement = input.statement.trim()
  const words = countWords(statement)
  const characters = statement.length
  const checks: CheckItem[] = []
  const paragraphs = statement.split(/\n\s*\n|\r\n\s*\r\n/).map((p) => p.trim()).filter(Boolean)

  // Criteria from the person specification
  const statementStems = new Set(tokens(statement).map(stem))
  const criteria: CriterionResult[] = parseCriteria(input.personSpec).map((text) => {
    const terms = keyTerms(text)
    const matched = terms.filter((t) => statementStems.has(stem(t)))
    const missing = terms.filter((t) => !statementStems.has(stem(t)))
    const share = terms.length ? matched.length / terms.length : 1
    return { text, matched, missing, status: share >= 0.5 ? 'covered' : matched.length > 0 ? 'partly' : 'missing' }
  })
  if (criteria.length) {
    const missing = criteria.filter((c) => c.status === 'missing').length
    const partly = criteria.filter((c) => c.status === 'partly').length
    checks.push({
      id: 'criteria',
      title: 'Covers the person specification',
      status: missing > 0 ? 'bad' : partly > 0 ? 'warn' : 'good',
      detail: missing > 0
        ? `${missing} of ${criteria.length} criteria don't seem to be mentioned at all. Shortlisters score each one, so give an example for every criterion assessed on the application.`
        : partly > 0
          ? `Every criterion is touched on, but ${partly} only partly. Check those have a clear example, not just a passing mention.`
          : `All ${criteria.length} criteria are mentioned. Make sure each has a real example behind it.`,
    })
  } else {
    checks.push({
      id: 'criteria',
      title: 'Covers the person specification',
      status: 'warn',
      detail: "Paste the person specification too, and we'll check every criterion is covered. It's the list shortlisters score you against.",
    })
  }

  // Length
  if (input.limit && input.limit > 0) {
    const used = input.limitUnit === 'characters' ? characters : words
    const share = used / input.limit
    checks.push({
      id: 'length',
      title: 'Fits the limit',
      status: share > 1 ? 'bad' : share < 0.5 ? 'warn' : 'good',
      detail: share > 1
        ? `${used.toLocaleString('en-GB')} ${input.limitUnit}: ${(used - input.limit).toLocaleString('en-GB')} over the ${input.limit.toLocaleString('en-GB')} limit. Cut descriptions of duties before you cut examples.`
        : share < 0.5
          ? `${used.toLocaleString('en-GB')} of ${input.limit.toLocaleString('en-GB')} ${input.limitUnit}. There's room for more evidence, if you have criteria without an example.`
          : `${used.toLocaleString('en-GB')} of ${input.limit.toLocaleString('en-GB')} ${input.limitUnit}.`,
    })
  } else {
    checks.push({
      id: 'length',
      title: 'Fits the limit',
      status: words > 1500 ? 'warn' : 'good',
      detail: `${words.toLocaleString('en-GB')} words, ${characters.toLocaleString('en-GB')} characters. Enter the limit shown on the application form to check it fits.`,
    })
  }

  // "I" not "we"
  const iCount = (statement.match(/\bI\b|\bI'(m|ve|d)\b|\bmy\b/g) || []).length
  const weCount = (statement.match(/\b(we|our|us)\b/gi) || []).length
  checks.push({
    id: 'first-person',
    title: 'Says what you did',
    status: weCount >= 2 && weCount >= iCount * 0.3 ? 'warn' : iCount === 0 ? 'bad' : 'good',
    detail: weCount >= 2 && weCount >= iCount * 0.3
      ? `"We" or "our" appears ${weCount} times. Shortlisters are scoring you, so say what you did: "I arranged", not "we arranged".`
      : iCount === 0
        ? "It doesn't say what you did. Write in the first person and describe your own actions."
        : 'Written about your own actions.',
  })

  // Results
  const withResults = paragraphs.filter((p) => RESULT_MARKERS.test(p)).length
  const exampleParagraphs = Math.max(paragraphs.length - 1, 1)
  checks.push({
    id: 'results',
    title: 'Examples have results',
    status: withResults === 0 ? 'bad' : withResults < Math.ceil(exampleParagraphs / 2) ? 'warn' : 'good',
    detail: withResults === 0
      ? 'None of your paragraphs says what happened as a result. End each example with the outcome, with a number if you have one.'
      : withResults < Math.ceil(exampleParagraphs / 2)
        ? `Only ${withResults} of ${paragraphs.length} paragraphs mention an outcome. Say what changed because of what you did.`
        : 'Most of your examples say what happened as a result.',
  })

  // Claims without evidence
  const lower = statement.toLowerCase()
  const claims = GENERIC_CLAIMS.filter((c) => lower.includes(c))
  checks.push({
    id: 'claims',
    title: 'Shows rather than tells',
    status: claims.length >= 3 ? 'bad' : claims.length > 0 ? 'warn' : 'good',
    detail: claims.length
      ? `Phrases like ${claims.slice(0, 3).map((c) => `"${c}"`).join(', ')} are claims every applicant makes. Replace each with a short example that proves it.`
      : 'No stock phrases found.',
  })

  // Readability
  const longParagraphs = paragraphs.filter((p) => countWords(p) > 180).length
  checks.push({
    id: 'structure',
    title: 'Easy to scan',
    status: paragraphs.length <= 1 && words > 250 ? 'bad' : longParagraphs > 0 ? 'warn' : 'good',
    detail: paragraphs.length <= 1 && words > 250
      ? "It's one long block. Split it into paragraphs, one per criterion, so a busy shortlister can tick them off."
      : longParagraphs > 0
        ? `${longParagraphs} paragraph${longParagraphs === 1 ? ' is' : 's are'} over 180 words. Shorter paragraphs, each starting with the criterion it answers, are easier to score.`
        : 'Broken into readable paragraphs.',
  })

  if (input.nhs) {
    const shown = NHS_VALUES.filter(([, re]) => re.test(statement)).map(([name]) => name)
    checks.push({
      id: 'values',
      title: 'Shows the NHS values',
      status: shown.length >= 3 ? 'good' : shown.length > 0 ? 'warn' : 'bad',
      detail: shown.length >= 3
        ? `Your examples reflect ${shown.length} of the six NHS values, including ${shown.slice(0, 2).join(' and ')}.`
        : `Shortlisters look for the NHS values: working together for patients, respect and dignity, commitment to quality of care, compassion, improving lives and everyone counts. Show them in your examples rather than listing them.`,
    })

    const identifying = /\b(Mr|Mrs|Ms|Miss|Mx)\.?\s+[A-Z][a-z]+|\bpatient(s)?\s+(called|named)\b|\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b/.test(statement)
    checks.push({
      id: 'confidentiality',
      title: 'Keeps patients anonymous',
      status: identifying ? 'bad' : 'good',
      detail: identifying
        ? 'It looks like it includes a name or a date that could identify a patient. Remove anything that could identify someone: confidentiality is part of what is being judged.'
        : 'No names or dates that could identify a patient found.',
    })
  }

  return { words, characters, checks, criteria }
}
